import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import {
  parseFieldSchema,
  useCopyToWorld,
  useTaxonomyCategories,
  useTaxonomyCategory,
  useTaxonomyEntries,
  useTaxonomyGroups,
  type TaxonomyCategoryDto,
} from '../api/queries/taxonomy';
import { useMyWorlds } from '../api/queries/worlds';
import { TaxonomyEntryCard } from '../components/taxonomy/TaxonomyEntryCard';
import { TaxonomyEntryDetail } from '../components/taxonomy/TaxonomyEntryDetail';

const PAGE_SIZE = 48;

export function ReferenceLibraryRoute() {
  const { data: groups = [], isLoading: groupsLoading } = useTaxonomyGroups();
  const { data: allCategories = [] } = useTaxonomyCategories();

  // Selection state
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [detailEntryId, setDetailEntryId] = useState<string | null>(null);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const multiSelectMode = selectedIds.size > 0;

  // Copy-to-world
  const { data: worlds = [] } = useMyWorlds();
  const copyToWorld = useCopyToWorld();
  const [copyMenuAnchor, setCopyMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  // Sidebar collapsed groups
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  // Category detail (for field schema)
  const { data: categoryDetail } = useTaxonomyCategory(
    selectedCategoryKey || undefined,
  );
  const fieldSchema = useMemo(
    () => (categoryDetail ? parseFieldSchema(categoryDetail.fieldSchema) : []),
    [categoryDetail],
  );

  // Entries query
  const { data: entriesResult, isLoading: entriesLoading } = useTaxonomyEntries(
    {
      categoryKey: selectedCategoryKey || undefined,
      search: search.trim() || undefined,
      page,
      pageSize: PAGE_SIZE,
    },
  );

  const entries = entriesResult?.items ?? [];
  const totalPages = entriesResult?.totalPages ?? 0;

  // Group categories by their group
  const categoriesByGroup = useMemo(() => {
    const map = new Map<string, TaxonomyCategoryDto[]>();
    for (const cat of allCategories) {
      const list = map.get(cat.groupKey) ?? [];
      list.push(cat);
      map.set(cat.groupKey, list);
    }
    return map;
  }, [allCategories]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleCategorySelect = useCallback((key: string) => {
    setSelectedCategoryKey(key);
    setPage(1);
    setSelectedIds(new Set());
  }, []);

  const toggleSelectEntry = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCopyToWorld = useCallback(
    async (worldId: string, entryIds?: string[]) => {
      const ids = entryIds ?? Array.from(selectedIds);
      if (ids.length === 0) return;
      await copyToWorld.mutateAsync({ worldId, entryIds: ids });
      setSelectedIds(new Set());
      setCopyMenuAnchor(null);
    },
    [selectedIds, copyToWorld],
  );

  const selectedCategory = allCategories.find(
    (c) => c.key === selectedCategoryKey,
  );

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Sidebar */}
      <Paper
        square
        variant="outlined"
        sx={{
          width: 260,
          minWidth: 260,
          overflow: 'auto',
          borderTop: 0,
          borderBottom: 0,
          borderLeft: 0,
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Reference Library
          </Typography>
        </Box>

        <List dense disablePadding>
          {/* "All" option — not selectable until a category is chosen */}
          {selectedCategoryKey && (
            <ListItemButton
              onClick={() => handleCategorySelect('')}
              sx={{ px: 2 }}
            >
              <ListItemText
                primary="All Categories"
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
              />
            </ListItemButton>
          )}

          {groupsLoading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {groups.map((group) => {
            const cats = categoriesByGroup.get(group.key) ?? [];
            const isCollapsed = collapsedGroups.has(group.key);
            return (
              <Box key={group.key}>
                <ListItemButton
                  onClick={() => toggleGroup(group.key)}
                  sx={{ px: 2, py: 0.5 }}
                >
                  <ListItemText
                    primary={group.name}
                    primaryTypographyProps={{
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                    }}
                  />
                  {isCollapsed ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ExpandLessIcon fontSize="small" />
                  )}
                </ListItemButton>
                <Collapse in={!isCollapsed}>
                  {cats.map((cat) => (
                    <ListItemButton
                      key={cat.key}
                      selected={cat.key === selectedCategoryKey}
                      onClick={() => handleCategorySelect(cat.key)}
                      sx={{ pl: 3, pr: 2, py: 0.25 }}
                    >
                      <ListItemText
                        primary={cat.name}
                        primaryTypographyProps={{
                          fontSize: 13,
                          noWrap: true,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {cat.entryCount}
                      </Typography>
                    </ListItemButton>
                  ))}
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Paper>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Top bar */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
              {selectedCategory?.name ?? 'Select a category'}
            </Typography>
            {multiSelectMode && (
              <Tooltip title="Toggle multi-select off">
                <IconButton
                  onClick={() => setSelectedIds(new Set())}
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {selectedCategoryKey && (
            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 1.5 }}
              alignItems="center"
            >
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                sx={{ width: 300 }}
              />
              {selectedCategory && (
                <Typography variant="body2" color="text.secondary">
                  {entriesResult?.total ?? 0} entries
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        {/* Card grid */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, pt: 1 }}>
          {!selectedCategoryKey && !entriesLoading && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Select a category from the sidebar to browse entries
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The Reference Library contains{' '}
                {allCategories
                  .reduce((sum, c) => sum + c.entryCount, 0)
                  .toLocaleString()}{' '}
                entries across {allCategories.length} categories
              </Typography>
            </Box>
          )}

          {entriesLoading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {selectedCategoryKey && !entriesLoading && entries.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No entries found{search ? ` matching "${search}"` : ''}.
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 2,
            }}
          >
            {entries.map((entry) => (
              <TaxonomyEntryCard
                key={entry.id}
                entry={entry}
                selected={selectedIds.has(entry.id)}
                onToggleSelect={toggleSelectEntry}
                onClick={(id) => setDetailEntryId(id)}
                multiSelectMode={multiSelectMode}
              />
            ))}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
              />
            </Box>
          )}
        </Box>

        {/* Bottom action bar — shown when items selected */}
        {multiSelectMode && (
          <Paper
            elevation={8}
            sx={{
              p: 1.5,
              px: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <CheckBoxIcon color="primary" fontSize="small" />
            <Typography variant="body2" fontWeight={600}>
              {selectedIds.size} selected
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedIds(new Set())}
              startIcon={<ClearIcon />}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={(e) => setCopyMenuAnchor(e.currentTarget)}
              disabled={copyToWorld.isPending}
            >
              {copyToWorld.isPending ? 'Copying...' : 'Add to World'}
            </Button>
            <Menu
              anchorEl={copyMenuAnchor}
              open={Boolean(copyMenuAnchor)}
              onClose={() => setCopyMenuAnchor(null)}
            >
              {worlds.length === 0 && (
                <MenuItem disabled>No worlds found</MenuItem>
              )}
              {worlds.map((w) => (
                <MenuItem key={w.id} onClick={() => handleCopyToWorld(w.id)}>
                  {w.name}
                </MenuItem>
              ))}
            </Menu>
          </Paper>
        )}
      </Box>

      {/* Detail drawer */}
      <TaxonomyEntryDetail
        entryId={detailEntryId}
        open={detailEntryId !== null}
        onClose={() => setDetailEntryId(null)}
        fieldSchema={fieldSchema}
        onCopyToWorld={
          worlds.length > 0
            ? (ids) => {
                // If only one world, copy directly; otherwise show picker
                if (worlds.length === 1) {
                  void handleCopyToWorld(worlds[0].id, ids);
                } else {
                  // Add to selection and let user pick via bottom bar
                  setSelectedIds(new Set(ids));
                  setDetailEntryId(null);
                }
              }
            : undefined
        }
        onNavigateToEntry={(id) => setDetailEntryId(id)}
      />
    </Box>
  );
}
